// libraries
import React, { useState } from "react";

// types
export interface DateOption {
  value: string;
  label: string;
}

export interface FilterOption {
  id: string;
  label: string;
  selected?: boolean;
}

export interface FilterGroupData {
  type: "type" | "tema" | "tag";
  label: string;
  options: FilterOption[];
}

export interface FiltersProps {
  dateFilter?: {
    value?: string;
    options?: DateOption[];
  };
  filters?: {
    type?: FilterGroupData;
    tema?: FilterGroupData;
    tag?: FilterGroupData;
  };
  selectedFilterIds?: {
    type?: string[];
    tema?: string[];
    tag?: string[];
  };
  onFilterChange: (
    filterId: string,
    paramType: "type" | "tema" | "tag"
  ) => void;
  onDateChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  className?: string;
  showOnlyDateFilter?: boolean;
  title?: string;
  showFiltersLabel?: string;
  hideFiltersLabel?: string;
  filtersContentId?: string;
}

export default function Filters({
  dateFilter,
  filters,
  selectedFilterIds,
  onFilterChange,
  onDateChange,
  className,
  showOnlyDateFilter = false,
  title = "Filter",
  showFiltersLabel = "Vis filter",
  hideFiltersLabel = "Skjul filter",
  filtersContentId = "filterContent",
}: FiltersProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const dateOptions = dateFilter?.options || [
    { value: "", label: "Filtrér på dato" },
    { value: "last30", label: "Sist mnd." },
    { value: "last90", label: "Siste 3 mnd." },
    { value: "last180", label: "Siste 6 mnd." },
    { value: "last360", label: "Siste år" },
    { value: "olderThan360", label: "Eldre enn 1 år" },
  ];

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const renderFilterGroup = (
    filterGroup: FilterGroupData | undefined,
    selectedIds: string[],
    className: string
  ) => {
    if (
      !filterGroup ||
      !filterGroup.options ||
      filterGroup.options.length === 0
    ) {
      return null;
    }

    return (
      <div className={className}>
        <h2>{filterGroup.label}</h2>
        <ul id={`${filterGroup.type}Filter`}>
          {filterGroup.options.map((option) => {
            const isChecked = selectedIds.includes(option.id);

            return (
              <li key={option.id}>
                <input
                  className="filterOption"
                  type="checkbox"
                  id={option.id}
                  name={filterGroup.type}
                  value={option.id}
                  checked={isChecked}
                  onChange={() => onFilterChange(option.id, filterGroup.type)}
                />
                <span className="filter-checkbox"></span>
                <label htmlFor={option.id} className="filter-label">
                  <span>{option.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <form action="#" className={className || "collapse in"} id="filterCollapse">
      <div className="filters-title">
        <h2>{title}</h2>
      </div>

      <button 
        type="button"
        className="filters__toggle button button--hollow button--primary" 
        aria-label={isExpanded ? hideFiltersLabel : showFiltersLabel}
        aria-expanded={isExpanded}
        aria-controls={filtersContentId}
        onClick={handleToggle}
      >
        {isExpanded ? hideFiltersLabel : showFiltersLabel}
      </button>

      <div className={isExpanded ? "filters-content filters-content--open" : "filters-content"} id={filtersContentId}>
        {/* Date Filter */}
        <div className="">
          <div className="select-form">
            <select
              id="dateSelectDropdown"
              name="date"
              value={dateFilter?.value || ""}
              onChange={onDateChange}
            >
              {dateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Groups */}
        {!showOnlyDateFilter && (
          <>
            {renderFilterGroup(
              filters?.type,
              selectedFilterIds?.type || [],
              "article-type"
            )}

            {renderFilterGroup(
              filters?.tema,
              selectedFilterIds?.tema || [],
              "theme-type"
            )}

            {renderFilterGroup(
              filters?.tag,
              selectedFilterIds?.tag || [],
              "theme-type"
            )}
          </>
        )}
      </div>
    </form>
  );
}
